import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { getUser } from "generated/api/user";

import { userFlagsKeys } from "./query-keys";

const userApi = getUser();

/**
 * Per-user boolean flags from GET /api/users/guides.
 * Keys are flag names (e.g. guide ids); values are boolean or null.
 * Narrowed from Orval's `{ [key: string]: unknown }` (backend ObjectNode).
 */
export type UserFlags = Record<string, boolean | null>;

const fetchUserFlags = async (): Promise<UserFlags> =>
  (await userApi.getGuides()) as UserFlags;

export const useUserFlags = <TData = UserFlags>(
  options?: Omit<UseQueryOptions<UserFlags, Error, TData>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    queryKey: userFlagsKeys.current(),
    queryFn: fetchUserFlags,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

const patchUserFlags = async (payload: UserFlags): Promise<UserFlags> =>
  (await userApi.patchGuides(payload)) as UserFlags;

export const useUpdateUserFlags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchUserFlags,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: userFlagsKeys.current() });
      const previous = queryClient.getQueryData<UserFlags>(userFlagsKeys.current());
      queryClient.setQueryData<UserFlags>(userFlagsKeys.current(), (old = {}) => ({
        ...old,
        ...payload,
      }));
      return { previous };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(userFlagsKeys.current(), context.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(userFlagsKeys.current(), data);
    },
  });
};
